/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import WebGLContext from "App/web_gl_context";
import RenderState from "App/render/webgl/render_state";
import View from "App/view/view";
import Shader from "App/render/webgl/shaders/shader";
import GLShader from "App/render/webgl/renderables/shader";
import IScene from "js_interface/src/scene_interface";

export default
class Pass {

    public state: RenderState;
    public view: View;

    protected _renderGraph: WebGLRenderGraph;
    protected _context: WebGLContext;
    protected _sceneAPI: IScene;
    protected _typeName: string = null;
    private _shaders: {};
    private _activeShaderRenderableIndex: number;
    protected _activeShader: Shader;
    protected _requireLighting: boolean;
    protected _shadersToBins: {};
    protected _somethingRendered: boolean;
    protected _activeShaderRenderable: GLShader;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        this._renderGraph = aRenderGraph;
        this._context = <WebGLContext>aRenderGraph.getContext();
        this._sceneAPI = aSceneAPI;

        this.state = new RenderState();

        this._shaders = {};
        this._activeShaderRenderableIndex = 0;
        this._activeShader = null;
        this._activeShaderRenderable = null;
        this.view = null;
        this._requireLighting = false;
        this._shadersToBins = {};
        this._somethingRendered = false;
    }

    public getClassTypeName(): string {
        return this._typeName;
    }

    public initialize() {
    }

    public attachShaderToBin(aBinTypeName: string, aShader: Shader) {
        this._shadersToBins[aBinTypeName] = aShader;
    }

    /**
     * @return {number}
     */
    public getActiveShaderRenderableIndex(): number {
        return this._activeShaderRenderableIndex;
    }

    public setActiveShaderRenderableIndex(aIndex: number) {
        this._activeShaderRenderableIndex = aIndex;
    }

    /**
     * Only for simplification
     *
     * @param {number} aIndex
     * @param {string} aBinName
     */
    public setShaderByIndex(aIndex: number, aBinName: string) {
        this.setActiveShaderRenderableIndex(aIndex);
        this.bindShader(aBinName);
        return this.getActiveShaderRenderable();
    };

    /**
     * Bind shader attached to bin
     *
     * @param {string} aBinTypeName
     */
    public bindShader(aBinTypeName: string) {
        this._activeShader = this._shadersToBins[aBinTypeName];

        this._activeShaderRenderable = <GLShader>this._activeShader.get(this._activeShaderRenderableIndex);
        this.state.setRenderablesFilters(this._activeShaderRenderable.getRenderablesFilters());

        this._activeShaderRenderable.render();
    }

    public unbindShader() {
        if (this._activeShaderRenderable) {
            this._activeShaderRenderable.finishRender();
        }

        this._activeShaderRenderable = null;
        this.state.resetRenderablesFilters();
    }

    /**
     * @return {Shader}
     */
    public getActiveShader(): Shader {
        return this._activeShader;
    }

    /**
     * @return {Shader|null}
     */
    public getActiveShaderRenderable(): GLShader {
        return this._activeShaderRenderable;
    }

    /**
     * @return {boolean}
     */
    public getRequireLighting(): boolean {
        return this._requireLighting;
    }

    public release() {
    }
}
